/**
 * OriginPill — did a person put this here, or did the AI find it?
 *
 * THE HIGHEST-VALUE FIX IN THIS TAB. Origin decided whether a source could be
 * trusted as the firm's own paper or had to be verified as a machine
 * suggestion, and it was visible only as a 10px caption in the grid view that
 * nobody used — the table had an Origin column that was never switched on. So
 * the distinction existed in the data and nowhere on screen.
 *
 * Champagne for uploaded (the brand accent, used the way the accent is meant
 * to be used: a small mark of "ours"), info blue for AI-found. Both always
 * rendered, so the absence of a pill never has to be interpreted.
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { PiCloudArrowUpLight, PiSparkleLight } from 'react-icons/pi';
import type { SourceOrigin } from './sourceModel';

interface Props {
  origin: SourceOrigin;
  /** Icon only, for tight rows. The label stays in the tooltip. */
  compact?: boolean;
  className?: string;
}

const OriginPill: React.FC<Props> = ({ origin, compact = false, className }) => {
  const { t } = useTranslation();
  const uploaded = origin === 'uploaded';

  const label = uploaded ? t('Uploaded') : t('AI-found');
  const tip = uploaded
    ? t('Added by someone on this matter')
    : t('Retrieved by AI during analysis');

  return (
    <span
      data-testid="origin-pill"
      data-origin={origin}
      title={tip}
      className={clsx(
        'inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5',
        'text-[10px] font-medium whitespace-nowrap',
        uploaded
          ? 'border-champagne/40 bg-champagne/10 text-champagne'
          : 'border-info-200 dark:border-info-800/60 bg-info-50 dark:bg-info-900/20 text-info-700 dark:text-info-300',
        className
      )}
    >
      {uploaded ? (
        <PiCloudArrowUpLight className="h-3 w-3 flex-shrink-0" />
      ) : (
        <PiSparkleLight className="h-3 w-3 flex-shrink-0" />
      )}
      {!compact && label}
    </span>
  );
};

export default OriginPill;
